import { Injectable, Inject } from '@angular/core';
import { Store, select, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, withLatestFrom, auditTime, filter, take } from 'rxjs/operators';

import * as Utils from '@shared/core/utils';
import * as Services from '@shared/core/services';
import * as Tokens from '@shared/core/tokens';

import * as actions from '../actions';
import * as selectors from '../selectors';
import { CollectionTypeParams } from './collection-type.models';

@Injectable()
export class CollectionTypesEffects {
    public onChangeLocationSelectDefaultOrderTypeForLocation$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.CurrentLocationSet),
            switchMap(() =>
                this._store.pipe(
                    select(selectors.routeIsLocationDetailsPage()),
                    filter((isCurrentRouteLocationDetails) => !!isCurrentRouteLocationDetails),
                    take(1),
                ),
            ),
            withLatestFrom(
                this._store.pipe(select(selectors.getCurrentLocationNo)),
                this._store.pipe(select(selectors.getCartOrderTypeId)),
                this._store.pipe(
                    select(selectors.getCollectionType),
                    filter((collectionTypeParams) => !Object.values(collectionTypeParams).every((param) => param === null)),
                ),
                this._store.pipe(select(selectors.isCollectionTypeDineIn)),
            ),
            switchMap(([, currentLocationNo, cartOrderTypeId, collection, isDineIn]) =>
                this._store.pipe(
                    select(selectors.getCollectionTypesListForLocation(currentLocationNo)),
                    filter((obj) => obj !== null && obj?.length > 0),
                    take(1),
                    switchMap((availableCollectionsForLocation) => {
                        const priorityOrderTypeId = collection.orderTypeId || cartOrderTypeId;
                        const collectionTypeConfig = new Utils.CollectionTypeHelper(this._config.collectionTypes).getCollectionTypeByOrderTypeId(priorityOrderTypeId);
                        let defaultCollectionTypeToSet = availableCollectionsForLocation?.find((obj) => obj.GroupType === collectionTypeConfig.collectionTypeId);

                        const collectionTypeParamObj: CollectionTypeParams = {
                            orderTypeId: null,
                            address: null,
                            details: null,
                        };

                        const isCartOrderTypeId = cartOrderTypeId && defaultCollectionTypeToSet?.OrderTypeIds.includes(cartOrderTypeId);
                        if (isCartOrderTypeId) {
                            collectionTypeParamObj.orderTypeId = priorityOrderTypeId;
                            collectionTypeParamObj.address = collection.address;
                            collectionTypeParamObj.details = collection.details;

                            return [actions.SetCollectionType({ ...collectionTypeParamObj, previousOrderTypeId: collection.orderTypeId })];
                        }

                        if (isDineIn) {
                            let foundDineInTypeInLocation = availableCollectionsForLocation?.find((obj) => obj.GroupType === OLO.Enums.COLLECTION_TYPE.DINE_IN);
                            defaultCollectionTypeToSet = foundDineInTypeInLocation || defaultCollectionTypeToSet;
                        }

                        if (defaultCollectionTypeToSet) {
                            collectionTypeParamObj.orderTypeId = defaultCollectionTypeToSet.OrderTypeIds[0];
                        }

                        if (!defaultCollectionTypeToSet) {
                            if (availableCollectionsForLocation?.length) {
                                const firstAvailableCollectionInLocation = availableCollectionsForLocation[0];
                                collectionTypeParamObj.orderTypeId = firstAvailableCollectionInLocation.OrderTypeIds[0];
                            }
                        }

                        if (collection.orderTypeId === collectionTypeParamObj.orderTypeId) return [];

                        return [actions.SetCollectionType(collectionTypeParamObj)];
                    }),
                ),
            ),
        ),
    );

    public onCustomOrderTypeSelectedUpdateValuesInCollectionState$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineOrderTypeUpdateValues),
            withLatestFrom(this._store.pipe(select(selectors.getSelectedOrderType))),
            filter(([, orderType]) => orderType !== null),
            switchMap(([action, orderType]) => {
                let arr: Action[] = [actions.PatchOrderTypeIdCollectionTypeValue({ orderTypeId: orderType.Id })];

                if (action.details || orderType.Details) {
                    arr.push(actions.PatchDetailsCollectionTypeValue({ details: action.details || orderType.Details }));
                } else {
                    arr = [actions.SetCollectionType(new CollectionTypeParams(orderType.Id))];
                }

                return arr;
            }),
        ),
    );

    public saveValuesInLocalStorage$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.PatchOrderTypeIdCollectionTypeValue, actions.PatchAddressCollectionTypeValue, actions.SetCollectionType, actions.PatchDetailsCollectionTypeValue),
            auditTime(10),
            withLatestFrom(this._store.pipe(select(selectors.getCollectionType))),
            switchMap(([, state]) => {
                Utils.Storage.set(OLO.Enums.USER_STORAGE.COLLECTION_TYPE, state);

                return [];
            }),
        ),
    );

    public showOrderTypeDetailsModalOnLocationOrCollectionTypeChange$: Observable<void> = createEffect(
        () =>
            combineLatest({
                currentLocationSet: this._actions$.pipe(ofType(actions.CurrentLocationSet)),
                setCollectionType: this._actions$.pipe(ofType(actions.SetCollectionType)),
            }).pipe(
                withLatestFrom(
                    this._store.pipe(select(selectors.getCartLocationNo)),
                    this._store.pipe(select(selectors.routeIsLocationDetailsPage())),
                    this._store.pipe(select(selectors.getCartOrderTypeId)),
                    this._store.pipe(select(selectors.isCollectionTypeDelivery)),
                    this._store.pipe(select(selectors.isCollectionTypeDineIn)),
                    this._store.pipe(select(selectors.getCurrentRouteQueryParams)),
                    this._store.pipe(select(selectors.getCollectionType)),
                    this._store.pipe(select(selectors.isMemberAuthorizedJWT)),
                    this._store.pipe(select(selectors.getCartDeliveryAddress)),
                ),
                auditTime(1000),
                switchMap(
                    ([
                        { currentLocationSet, setCollectionType },
                        cartLocationNo,
                        isLocationDetailsPage,
                        cartOrderTypeId,
                        isDelivery,
                        isDineIn,
                        queryParams,
                        collectionType,
                        isMember,
                        cartDelieveryAddress,
                    ]) => {
                        const orderTypeDetailsModal = Utils.DynamicPages.getCurrentPageSetup(
                            this._config.locationDetailsPage,
                            setCollectionType.orderTypeId,
                        )?.orderTypeDetailsModal;
                        const cartLocationIsCurrentLocationOrNotSet = cartLocationNo === null || currentLocationSet.locationNo === cartLocationNo;
                        const cartOrderTypeIdIsCurrentOrderTypeOrNotSet = cartOrderTypeId === null || setCollectionType.orderTypeId === cartOrderTypeId;

                        return this._store.pipe(
                            select(selectors.getOrderTypeDetailsDefinitionModel(setCollectionType.orderTypeId)),
                            filter(
                                (orderTypeDetailsDefinitionModel) =>
                                    isLocationDetailsPage && cartLocationIsCurrentLocationOrNotSet && cartOrderTypeIdIsCurrentOrderTypeOrNotSet && !!orderTypeDetailsModal,
                            ),
                            switchMap((orderTypeDetailsDefinitionModel) => {
                                const orderTypeDetailsDefinitionModelExtended: APICommon.OrderTypeDetailDefinitionExtended[] =
                                    collectionType?.details || orderTypeDetailsDefinitionModel;

                                if (isDineIn && queryParams?.tableNo) {
                                    orderTypeDetailsDefinitionModelExtended[0]._Value = queryParams.tableNo;
                                }

                                if (isDelivery && !cartDelieveryAddress) {
                                    if (isMember) {
                                        return this._modalsService.show({
                                            type: 'check-delivery-address-member',
                                            locationNo: currentLocationSet.locationNo,
                                            params: {
                                                orderTypeDetailsModal,
                                            },
                                        });
                                    } else {
                                        this._modalsService.show({
                                            type: 'check-delivery-address-guest',
                                            locationNo: currentLocationSet.locationNo,
                                            params: {
                                                orderTypeDetailsModal,
                                            },
                                        });
                                    }
                                } else if (!isDelivery) {
                                    this._modalsService.show({
                                        type: 'order-type-details',
                                        locationNo: currentLocationSet.locationNo,
                                        params: {
                                            orderTypeDetailsModal,
                                            orderTypeDetailsDefinitionModel: orderTypeDetailsDefinitionModelExtended,
                                        },
                                    });
                                }

                                return [];
                            }),
                        );
                    },
                ),
            ),
        { dispatch: false },
    );

    public setupCurrentCollectionTypeBasedOnRoutingParams$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.ROUTER_NAVIGATED),
            switchMap(() => this._store.pipe(select(selectors.getHistoryRoute))),
            switchMap((routerHistory) => (!routerHistory.length ? this._store.pipe(select(selectors.getCollectionTypeOrderTypeIdFromCurrentRoute)) : [])),
            withLatestFrom(this._store.pipe(select(selectors.getCollectionType))),
            switchMap(([orderTypeIdFromRoute, collectionType]) => {
                const collectionTypeHelper = new Utils.CollectionTypeHelper(this._config.collectionTypes);
                const urlParams: URLSearchParams = new URLSearchParams(window.location.search);

                if (typeof orderTypeIdFromRoute === 'number' && !!collectionTypeHelper.hasAnyCollectionTypeConfigOrderTypeId(orderTypeIdFromRoute)) {
                    return of(actions.SetCollectionType(new CollectionTypeParams(orderTypeIdFromRoute)));
                }
                const orderTypeIdFromUrl = +urlParams.get('orderTypeId');
                const orderTypeIdFromCollectionTypeDefaultConfig = collectionTypeHelper.getOrderTypeIdFromDefaultCollectionType();
                const collectionTypeConfigByUrlOrderTypeId = collectionTypeHelper.getCollectionTypeByOrderTypeId(orderTypeIdFromUrl);

                if (orderTypeIdFromUrl && collectionTypeConfigByUrlOrderTypeId) {
                    return of(actions.SetCollectionType(new CollectionTypeParams(orderTypeIdFromUrl)));
                }

                if (!collectionType.orderTypeId) {
                    return of(actions.SetCollectionType(new CollectionTypeParams(orderTypeIdFromCollectionTypeDefaultConfig, collectionType.address)));
                }

                return [];
            }),
        ),
    );

    public showWarningForDeliveryAndAddressProvider$: Observable<void> = createEffect(
        () =>
            this._actions$.pipe(
                ofType(actions.SetCollectionType),
                withLatestFrom(this._store.pipe(select(selectors.isCollectionTypeDelivery))),
                auditTime(100),
                switchMap(([, isDelivery]) => {
                    if (isDelivery && !this._config.addressProvider.enabled) {
                        console.warn('WARNING! You must enable address provider to support delivery type!');
                    }

                    return [];
                }),
            ),
        { dispatch: false },
    );

    public clearValuesInLocalStorage$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.ResetCollectionTypeAddressAndDetails),
            switchMap(() => {
                Utils.Storage.remove(OLO.Enums.USER_STORAGE.COLLECTION_TYPE);

                return [];
            }),
        ),
    );

    constructor(
        @Inject(Tokens.STATIC_TEXT_TOKEN) public readonly t: T.StaticTexts,
        @Inject(Tokens.CONFIG_TOKEN) private _config: OLO.Config,
        private _actions$: Actions,
        private _store: Store<OLO.State>,
        private _modalsService: Services.ModalsService,
    ) {}
}
